import React, { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import CommonButton from './CommonButton';
import CommonInput from './CommonInput';
import CommonModal from './CommonModal';
import { ApiError, changePassword, login } from '../api';
import { showToast } from '../utils/toast';

export interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  currentUserEmail: string;
}

type Step = 'verify' | 'set';

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose, currentUserEmail }) => {
  const [step, setStep] = useState<Step>('verify');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const reset = (): void => {
    setStep('verify');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleClose = (): void => {
    if (isVerifying || isSaving) return;
    reset();
    onClose();
  };

  // Reuses the login endpoint purely to confirm the typed password is
  // correct — token/response are discarded, nothing about the active
  // session is touched. This is the real "verify current password" step;
  // there's no separate backend endpoint for it since changePassword
  // already re-checks it anyway before actually changing anything.
  const handleVerify = async (): Promise<void> => {
    if (!currentPassword) {
      showToast('warning', 'Enter your current password.');
      return;
    }

    try {
      setIsVerifying(true);
      const response = await login({ email: currentUserEmail, password: currentPassword });
      if (!response.success) {
        throw new Error(response.message || 'Current password is incorrect.');
      }
      setStep('set');
    } catch (error) {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Current password is incorrect.';
      showToast('error', message);
    } finally {
      setIsVerifying(false);
    }
  };

  const isPasswordLongEnough = newPassword.length >= 8;
  const passwordsMatch = newPassword !== '' && newPassword === confirmPassword;

  const handleSave = async (): Promise<void> => {
    if (!isPasswordLongEnough) {
      showToast('warning', 'New password must be at least 8 characters.');
      return;
    }
    if (!passwordsMatch) {
      showToast('warning', 'Passwords do not match.');
      return;
    }

    try {
      setIsSaving(true);
      const response = await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      if (!response.success) {
        throw new Error(response.message || 'Unable to change password.');
      }

      showToast('success', response.message || 'Password changed successfully.');
      reset();
      onClose();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Unable to change password.';
      showToast('error', message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CommonModal
      open={open}
      title="Change Password"
      onClose={handleClose}
      maxWidth="sm"
      actions={
        step === 'verify' ? (
          <>
            <CommonButton label="Cancel" variant="outlined" disabled={isVerifying} onClick={handleClose} />
            <CommonButton label={isVerifying ? 'Verifying...' : 'Verify'} disabled={isVerifying} onClick={() => void handleVerify()} />
          </>
        ) : (
          <>
            <CommonButton label="Cancel" variant="outlined" disabled={isSaving} onClick={handleClose} />
            <CommonButton
              label={isSaving ? 'Saving...' : 'Change Password'}
              disabled={isSaving || !isPasswordLongEnough || !passwordsMatch}
              onClick={() => void handleSave()}
            />
          </>
        )
      }
    >
      {step === 'verify' ? (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Enter your current password to continue.
          </Typography>
          <CommonInput
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={setCurrentPassword}
            disabled={isVerifying}
            required
          />
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Current password verified. Set your new password below.
          </Typography>
          <CommonInput
            label="New Password"
            type="password"
            value={newPassword}
            onChange={setNewPassword}
            disabled={isSaving}
            required
            error={newPassword.length > 0 && !isPasswordLongEnough}
            helperText={newPassword.length > 0 && !isPasswordLongEnough ? 'Password must be at least 8 characters' : ''}
          />
          <CommonInput
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            disabled={isSaving}
            required
            error={confirmPassword.length > 0 && !passwordsMatch}
            helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : ''}
          />
        </Stack>
      )}
    </CommonModal>
  );
};

export default ChangePasswordModal;
